import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import TicketBadge from "@common/components/TicketBadge";
import { Device } from "@services/devices/types";
import { Room } from "@services/rooms/types";
import IconButton from "@common/components/IconButton";
import { DeviceProvisioningDispatchContext } from "@pages/Assets/DeviceProvisioning/context";
import { canBulkChange } from "@common/utils/user";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { getDeviceMonitoringSourcesStatus } from "@common/utils/devices/monitoringSources/getDeviceMonitoringSourcesStatus";
import { DeviceCardMonitoringStatus } from "@common/components/DeviceCardMonitoringStatus/DeviceCardMonitoringStatus";

type DeviceCardHeaderProps = {
  device?: Device;
  room?: Room;
  hideStatuses?: boolean;
  isArchivedDevice?: boolean;
  previousPage?: string;
  deviceId?: string;
};

const DeviceCardHeader = ({
  device,
  room,
  hideStatuses = false,
  isArchivedDevice,
  previousPage,
  deviceId,
}: DeviceCardHeaderProps) => {
  const { t } = useTranslation();
  const { setOpen } = useContext(DeviceProvisioningDispatchContext);
  const numOpenTickets = device?.numOpenTickets
    ? device.numOpenTickets
    : room?.numOpenTickets
    ? room.numOpenTickets
    : 0;
  const [user] = useAuthenticatedUser();
  const canBulk = canBulkChange(user);

  const deviceMonitoringSourceStatuses = getDeviceMonitoringSourcesStatus({
    device,
    t,
    isDeviceCard: true,
  }).flatMap((item) => item.itemsMonitoring);

  return (
    <Grid
      container
      direction="row-reverse"
      wrap="nowrap"
      sx={{
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
        py: 1,
        px: 2,
        gap: 2,
      }}
    >
      <Grid
        sx={{
          visibility: numOpenTickets > 0 ? "visible" : "hidden",
        }}
      >
        <TicketBadge
          notificationsNumber={numOpenTickets}
          previousPage={previousPage}
          deviceId={deviceId ?? ""}
        />
      </Grid>
      {!hideStatuses &&
        !isArchivedDevice &&
        (device?.provisioned ? (
          <DeviceCardMonitoringStatus
            device={device}
            previousPage={previousPage}
            items={deviceMonitoringSourceStatuses}
          />
        ) : canBulk ? (
          <Stack
            direction="row"
            sx={{
              gap: 0.5,
            }}
          >
            <IconButton onClick={() => device?.id && setOpen(device.id)}>
              <AddCircleIcon fontSize="large" />
            </IconButton>
          </Stack>
        ) : undefined)}
    </Grid>
  );
};

export default DeviceCardHeader;
